import {Component, EventEmitter, HostListener, Input} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {PermissionService} from "../../../_auth/permission.service";
import {ModelBase} from "../../../models/model-base";
import {SettingsPageModel} from "../../../models/settings-page-model";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {AppRoutes} from "../../../enums/routes";
import {ObservableQueueElement} from "../../../models/observable-queue-element";
import {concatMap, finalize, from, Observable} from "rxjs";
import {HttpPropertyChangeStatus} from "../../../enums/http-property-change-status";
import {SettingsInputElementModel} from "../../../models/settings-input-element-model";
import {SettingsInputElementComponent} from "../settings-input-element/settings-input-element.component";
import {CommonModule} from "@angular/common";


@Component({
  selector: 'app-settings-page',
  templateUrl: './settings-page.component.html',
  imports: [
    CommonModule,
    SettingsInputElementComponent
  ],
  styleUrls: ['./settings-page.component.scss']
})
export class SettingsPageComponent {
  public model?: ModelBase;
  public modelChanged = new EventEmitter<ModelBase>();
  public timeoutClick: boolean = false;
  observableQueue: ObservableQueueElement[] = [];
  redirectRoute: string = AppRoutes.Home;
  public requiresChanges: HttpPropertyChangeStatus[] = [];
  deleteModel: Function | undefined = undefined;
  deletePermission: boolean = false;
  @Input() settingsInputElementModel: { [key: string]: SettingsPageModel } = {};
  public settingsInputElementModelChanged = new EventEmitter<{ [key: string]: SettingsPageModel }>();
  public isVisible: boolean = true;

  constructor(public route: ActivatedRoute,
              public PermissionService: PermissionService,
              public router: Router,
              public globalAlertService: GlobalAlertService) {
  }

  public get sortedInputElements(): SettingsPageModel[] {
    let result: SettingsPageModel[] = [];
    for (let key in this.settingsInputElementModel) {
      result.push(this.settingsInputElementModel[key]);
    }
    return result.sort((a, b) => a.order - b.order);

  }

  @Input() loadInputModel: Function = () => {
  };

  @Input() saveChanges: Function = () => {
  };

  @Input() redirect: Function = (redirectRoute: string | undefined) => {
    if (redirectRoute !== undefined)
      this.redirectRoute = redirectRoute;
    window.location.href = this.redirectRoute;
  };

  deleteModelWrapper: Function = () => {
    if (this.deleteModel === undefined) return;
    this.deleteModel();
    setTimeout(() => {
        this.redirect();
      }
      , 2000);

  };

  @HostListener('document:keydown.enter', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if ((event.target as any).type == "textarea") return; // to prevent saving on multiline textareas
    this.saveChangesWrapper();
  }

  saveChangesWrapper() {
    this.observableQueue = [];
    this.saveChanges()
  }


  loadModelDataWrapper(id: string) {
    this.loadInputModel(id);
  }

  executeQueueWrapper() {
    let index = 0;
    from(this.observableQueue).pipe(
      concatMap((observable, observableIndex) => {
        index = observableIndex;
        return observable.observable
      }),
      finalize(() => {

        const allSucceeded = this.observableQueue.every(x => x.successIndicator);
        if (allSucceeded) {
          this.globalAlertService.createAlertBannerModel("Erfolgreich", "Die Änderungen wurden erfolgreich gespeichert.", AlertLevel.success, 5000);
          this.globalAlertService.show();
          // Asked BEFORE `queueSettled`, which is where a screen consumes whatever made it answer `false`.
          if (this.redirectAfterSave()) {
            setTimeout(() => {
                this.redirect();
              }
              , 2000);
          }
        }
        this.queueSettled(allSucceeded);
      })
    ).subscribe(result => {
      this.observableQueue[index].callback(result);
    }, error => {
      this.observableQueue[index].callbackError(error);
    });
  }

  /**
   * Called once the whole save queue has settled, **after** the shared success banner has been posted.
   *
   * A no-op here; a screen overrides it when one of its saves can succeed and still have something to say —
   * the company alias is the case it was written for (#637): `SetAlias` answers `200` with a `dnsWarning` when
   * the alias was stored but its DNS record was not.
   *
   * It exists because the ordering cannot be got right from a queue callback. Every element's `callback` runs
   * before this `finalize`, so a warning posted from there is overwritten by the success banner that follows —
   * and deferring it with a `setTimeout` from a callback races the *remaining* requests in the queue, which
   * arrive in macrotasks of their own. This is the one point at which "the queue is done" is a fact.
   *
   * @param allSucceeded whether every queued call reported success — the same condition the success banner
   *                     uses, passed rather than re-derived so an override cannot drift from it.
   */
  protected queueSettled(allSucceeded: boolean): void {
  }

  /**
   * Whether a fully successful save should leave the screen for {@link redirectRoute} after two seconds.
   *
   * `true` here, which is every settings screen's behaviour and stays so. A screen overrides it when a save
   * can succeed and still leave something on the page worth reading — the company alias (#637): the redirect
   * is `window.location.href`, i.e. a **full page reload**, so it takes the global alert banner with it, and a
   * DNS warning that has just been raised would be gone in two seconds.
   *
   * Asked *before* {@link queueSettled} runs, so an override can read the same state that override consumes.
   */
  protected redirectAfterSave(): boolean {
    return true;
  }

  parseToNumber(value: string | undefined): number | undefined {
    if (value === undefined || value === "") return undefined;
    value = value.toString();
    value = value.replace(",", ".");
    let result = Number(value);
    return isNaN(result) ? undefined : result
  }

  isValidNumber(value: string | undefined): boolean {

    if (value === undefined || value === "") {
      this.globalAlertService.createAlertBannerModel("Ungültige Eingabe", "Bitte geben Sie eine Zahl ein", AlertLevel.error, 5000);
      this.globalAlertService.show();
      return false;
    }
    try {
      let result = Number(value);
      return result !== undefined;
    } catch (e) {
      this.globalAlertService.createAlertBannerModel("Ungültige Eingabe", "Bitte geben Sie eine Zahl ein. Fehlerbehaftete Eingabe: " + value, AlertLevel.error, 5000);
      this.globalAlertService.show();
      return false;
    }
  }

  abortChangesWrapper() {
    this.redirect()
  }


  updateElements() {
    this.isVisible = false;
    setTimeout(() => {
      this.isVisible = true;
    }, 0);
  }

  changeRequestRequired(): boolean {
    if (this.requiresChanges.every(x => x === HttpPropertyChangeStatus.NotChanged)) {
      this.globalAlertService.createAlertBannerModel("Keine Änderungen", "Es wurden keine Änderungen vorgenommen.", AlertLevel.info, 5000);
      this.globalAlertService.show();
      return false;
    }
    return true;
  }

  saveValueProperty(name: string, cModel: SettingsInputElementModel, observable: (id: string, property: string, newValue: any) => Observable<any>): HttpPropertyChangeStatus {
    if (this.model === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (cModel.changeableProperties.newPropertyValue === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (cModel.changeableProperties.newPropertyValue === cModel.originalPropertyValue) return HttpPropertyChangeStatus.NotChanged;
    let observableQueueElement: ObservableQueueElement = new ObservableQueueElement();
    observableQueueElement.observable = observable(this.model.id, name, cModel.changeableProperties.newPropertyValue);
    observableQueueElement.callback = (model: ModelBase) => {
      this.model = model;
      cModel.originalPropertyValue = cModel.changeableProperties.newPropertyValue;
      observableQueueElement.successIndicator = true;
      this.updateElements();
    };
    observableQueueElement.callbackError = (error: any) => {
      this.globalAlertService.createAndShow("Fehler", "Fehler beim Speichern des Werts.", AlertLevel.error, 5000);
      observableQueueElement.successIndicator = false;
    };

    this.observableQueue.push(observableQueueElement);
    return HttpPropertyChangeStatus.Changed;
  }


  ngOnInit() {
    this.route.queryParams.subscribe(params => {
        this.loadModelDataWrapper(params["id"]);
      }
    );
  }
}

